(() => {
  'use strict';

  const EVENT_FIELDS = new Set([
    'content_id', 'platform', 'problem_id', 'landing_variant',
    'campaign_id', 'component', 'breed_key', 'breed_size',
    'route_key', 'destination_path',
    'source_key', 'medium_key', 'account_key', 'placement_key',
    'content_key', 'campaign_key', 'landing_key', 'entry_key'
  ]);

  const readAttributionContext = () => {
    try {
      const controller = window.WAGNUDGE_ATTRIBUTION_ENTRY_V1;
      return controller && typeof controller.getContext === 'function'
        ? controller.getContext() || {}
        : {};
    } catch (_error) {
      return {};
    }
  };

  const analyticsAllowed = () => {
    try {
      return Boolean(
        window.Shopify &&
        window.Shopify.customerPrivacy &&
        typeof window.Shopify.customerPrivacy.analyticsProcessingAllowed === 'function' &&
        window.Shopify.customerPrivacy.analyticsProcessingAllowed()
      );
    } catch (_error) {
      return false;
    }
  };

  const emit = (name, payload) => {
    if (!analyticsAllowed()) return;
    const detail = {};
    Object.entries({ ...readAttributionContext(), ...payload }).forEach(([key, value]) => {
      if (EVENT_FIELDS.has(key) && typeof value === 'string' && value.length > 0) detail[key] = value;
    });
    // The first DOM dispatch must satisfy the contract, before any bridge runs.
    if (!['content_id', 'platform', 'problem_id', 'landing_variant']
      .every((key) => typeof detail[key] === 'string' && detail[key].trim().length > 0)) return;
    window.dispatchEvent(new CustomEvent(name, { detail }));
  };

  const instances = new WeakMap();

  const init = (root) => {
    if (instances.has(root)) return;
    // A copied/stale DOM marker is not evidence that this node has listeners.
    delete root.dataset.selectorReady;

    const campaignId = root.dataset.campaignId || '';
    const cards = Array.from(root.querySelectorAll('[data-breed-card]'));
    const filters = Array.from(root.querySelectorAll('[data-breed-filter]'));
    const routeLinks = Array.from(root.querySelectorAll('[data-route-link]'));
    const problems = root.querySelector('[data-breed-problems]');
    const problemsHeading = root.querySelector('[data-breed-problems-heading]');
    const contextMessage = root.querySelector('[data-breed-context-message]');
    const status = root.querySelector('[data-breed-status]');
    const primaryCta = root.querySelector('[data-selector-primary-cta]');
    const stepOne = root.querySelector('[data-breed-step-one]');
    const viewAll = root.querySelector('[data-breed-view-all]');
    const changeDog = root.querySelector('[data-breed-change]');
    const selectionSummary = root.querySelector('[data-breed-selection-summary]');
    const selectionName = root.querySelector('[data-breed-selection-name]');
    const selectionSize = root.querySelector('[data-breed-selection-size]');
    const selectionImageWrap = root.querySelector('[data-breed-selection-image-wrap]');
    const selectionImage = root.querySelector('[data-breed-selection-image]');
    if (!cards.length || !filters.length || !viewAll || !stepOne ||
        !root.querySelector('[data-breed-grid]') || !problems || !problemsHeading ||
        !contextMessage || !status) return;

    const listeners = [];
    const timers = new Set();
    const listen = (target, type, handler) => {
      target.addEventListener(type, handler);
      listeners.push(() => target.removeEventListener(type, handler));
    };
    const schedule = (callback, delay) => {
      const timer = window.setTimeout(() => {
        timers.delete(timer);
        callback();
      }, delay);
      timers.add(timer);
    };
    const cleanup = () => {
      listeners.splice(0).forEach((remove) => remove());
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
      instances.delete(root);
      delete root.dataset.selectorReady;
    };
    const defaultContextMessage = contextMessage ? contextMessage.textContent : '';
    const breedMap = new Map(cards.map((card) => [card.dataset.breedKey, card]));
    const basePayload = {
      campaign_id: campaignId,
      content_id: root.dataset.contentId || '',
      platform: root.dataset.platform || '',
      problem_id: root.dataset.problemId || '',
      landing_variant: root.dataset.landingVariant || ''
    };
    let activeFilter = 'all';
    let allBreedsVisible = false;

    const restoreFilterFromUrl = () => {
      const requested = new URL(window.location.href).searchParams.get('breed_size') || 'all';
      activeFilter = ['small', 'medium', 'large', 'size-varies'].includes(requested) ? requested : 'all';
      filters.forEach((item) => {
        item.setAttribute('aria-pressed', String((item.dataset.breedFilter || 'all') === activeFilter));
      });
    };

    const sizeLabel = (value) => value === 'size-varies'
      ? 'Size varies'
      : value === 'not-provided' ? 'Size not provided' : `${value.charAt(0).toUpperCase()}${value.slice(1)}`;

    const applyBrowseDisplay = () => {
      cards.forEach((card) => {
        const unsure = card.dataset.breedKey === 'mixed-or-unsure';
        const matchesFilter = activeFilter === 'all' || card.dataset.breedSize === activeFilter;
        const defaultVisible = card.dataset.defaultVisible === 'true';
        card.hidden = !unsure && (!matchesFilter || (activeFilter === 'all' && !allBreedsVisible && !defaultVisible));
      });
      if (viewAll) {
        viewAll.hidden = activeFilter !== 'all';
        viewAll.setAttribute('aria-expanded', String(allBreedsVisible));
        viewAll.textContent = allBreedsVisible ? 'Show featured breeds' : 'View all 16 breeds';
      }
    };

    const showBrowseStep = (options = {}) => {
      cards.forEach((item) => item.setAttribute('aria-pressed', 'false'));
      if (stepOne) stepOne.hidden = false;
      if (selectionSummary) selectionSummary.hidden = true;
      if (contextMessage) contextMessage.textContent = defaultContextMessage;
      if (status) status.textContent = 'Breed selection cleared. Step 1 is ready.';
      applyBrowseDisplay();
      updateRouteLinks();
      if (options.updateHistory !== false) {
        const url = new URL(window.location.href);
        url.searchParams.delete('breed');
        if (activeFilter === 'all') url.searchParams.delete('breed_size');
        else url.searchParams.set('breed_size', activeFilter);
        url.hash = 'breed-selector-grid';
        window.history.pushState({ breed: '' }, '', `${url.pathname}${url.search}${url.hash}`);
      }
      if (options.moveFocus !== false && stepOne) {
        stepOne.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start'
        });
        schedule(() => (viewAll || cards[0]).focus({ preventScroll: true }), 0);
      }
    };

    const updateRouteLinks = (breedKey = '', breedSize = '') => {
      routeLinks.forEach((link) => {
        const destination = link.dataset.destinationPath || '';
        if (!destination.startsWith('/')) return;
        const url = new URL(destination, window.location.origin);
        if (breedKey) url.searchParams.set('breed', breedKey);
        if (breedSize && breedSize !== 'not-provided') url.searchParams.set('breed_size', breedSize);
        link.href = `${url.pathname}${url.search}`;
      });
    };

    const selectBreed = (breedKey, options = {}) => {
      const card = breedMap.get(breedKey);
      if (!card) {
        showBrowseStep({ updateHistory: false, moveFocus: false });
        return false;
      }

      cards.forEach((item) => item.setAttribute('aria-pressed', String(item === card)));
      const breedName = card.dataset.breedName || 'your dog';
      const breedSize = card.dataset.breedSize || '';
      const dogLabel = breedName === 'Mixed breed or not sure' ? 'your dog' : `a ${breedName}`;
      contextMessage.textContent = `You’re choosing guidance for ${dogLabel}. Breed gives context; the next question is what you’re noticing.`;
      status.textContent = `${breedName} selected. Step 2 is ready.`;
      updateRouteLinks(breedKey, breedSize);
      if (stepOne) stepOne.hidden = true;
      if (selectionSummary) selectionSummary.hidden = false;
      if (selectionName) selectionName.textContent = breedName;
      if (selectionSize) selectionSize.textContent = sizeLabel(breedSize);
      const sourceImage = card.querySelector('img');
      if (selectionImage && selectionImageWrap && sourceImage) {
        selectionImage.src = sourceImage.currentSrc || sourceImage.src;
        selectionImage.alt = sourceImage.alt;
        selectionImageWrap.hidden = false;
      } else if (selectionImageWrap) {
        selectionImageWrap.hidden = true;
      }

      if (options.updateHistory !== false) {
        const url = new URL(window.location.href);
        url.searchParams.set('breed', breedKey);
        if (breedSize && breedSize !== 'not-provided') url.searchParams.set('breed_size', breedSize);
        else url.searchParams.delete('breed_size');
        url.hash = 'breed-problems';
        window.history.pushState({ breed: breedKey, breed_size: breedSize }, '', `${url.pathname}${url.search}${url.hash}`);
      }

      if (options.emitEvent !== false) {
        emit(
          breedKey === 'mixed-or-unsure' ? 'wagn:mixed_breed_selected' : 'wagn:breed_selected',
          { ...basePayload, breed_key: breedKey, breed_size: breedSize, component: 'breed_grid' }
        );
      }

      if (options.moveFocus !== false && problemsHeading) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        problems.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        schedule(() => problemsHeading.focus({ preventScroll: true }), reduceMotion ? 0 : 350);
      }
      return true;
    };

    try {
      cards.forEach((card) => {
        listen(card, 'click', (event) => {
          event.preventDefault();
          selectBreed(card.dataset.breedKey || '');
        });
        listen(card, 'keydown', (event) => {
          if (event.key === ' ') {
            event.preventDefault();
            card.click();
          }
        });
      });

      filters.forEach((filter) => {
        listen(filter, 'click', () => {
          const value = filter.dataset.breedFilter || 'all';
          activeFilter = value;
          filters.forEach((item) => item.setAttribute('aria-pressed', String(item === filter)));
          applyBrowseDisplay();
          emit('wagn:breed_size_filter', { ...basePayload, breed_size: value, component: 'size_filter' });
        });
      });

      if (viewAll) {
        listen(viewAll, 'click', () => {
          allBreedsVisible = !allBreedsVisible;
          applyBrowseDisplay();
        });
      }

      if (changeDog) listen(changeDog, 'click', () => showBrowseStep());

      routeLinks.forEach((link) => {
        listen(link, 'click', () => {
          const destination = link.dataset.destinationPath || '';
          emit('wagn:breed_problem_selected', {
            ...basePayload,
            breed_key: new URL(window.location.href).searchParams.get('breed') || '',
            route_key: link.dataset.routeKey || '',
            component: 'problem_routes',
            destination_path: destination.startsWith('/') ? destination : ''
          });
        });
      });

      if (primaryCta) {
        listen(primaryCta, 'click', (event) => {
          const grid = root.querySelector('[data-breed-grid]');
          if (!grid) return;
          event.preventDefault();
          grid.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start'
          });
        });
      }

      listen(window, 'popstate', () => {
        const breedKey = new URL(window.location.href).searchParams.get('breed') || '';
        if (breedKey) selectBreed(breedKey, { updateHistory: false, emitEvent: false, moveFocus: false });
        else {
          restoreFilterFromUrl();
          showBrowseStep({ updateHistory: false, moveFocus: false });
        }
      });

      const initialBreed = new URL(window.location.href).searchParams.get('breed') || '';
      restoreFilterFromUrl();
      if (initialBreed) {
        selectBreed(initialBreed, { updateHistory: false, emitEvent: false, moveFocus: false });
      } else {
        updateRouteLinks();
        applyBrowseDisplay();
      }

      instances.set(root, cleanup);
      root.dataset.selectorReady = 'true';
    } catch (error) {
      cleanup();
      throw error;
    }
    emit('wagn:breed_selector_view', { ...basePayload, component: 'breed_selector' });
  };

  const rootsWithin = (scope) => {
    const roots = Array.from(scope.querySelectorAll('[data-kiki-breed-selector]'));
    if (scope.matches && scope.matches('[data-kiki-breed-selector]')) roots.unshift(scope);
    return roots;
  };
  const boot = () => rootsWithin(document).forEach(init);
  document.addEventListener('shopify:section:load', (event) => {
    rootsWithin(event.target).forEach(init);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    rootsWithin(event.target).forEach((root) => {
      const cleanup = instances.get(root);
      if (cleanup) cleanup();
    });
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
